import { IApplyingCompany } from "./applyingCompany";
import { IUser } from "./user";

export enum CustomerPreferredRateEnum {
  FIRST_MONTH_RATE = "FIRST_MONTH_RATE",
  MONTHLY_INTEREST_RATE = "MONTHLY_INTEREST_RATE",
  YEARLY_INTEREST_RATE = "YEARLY_INTEREST_RATE",
}

export const RATE_TO_BID_KEY_MAP = {
  [CustomerPreferredRateEnum.FIRST_MONTH_RATE]: "firstMonthInterestRate",
  [CustomerPreferredRateEnum.MONTHLY_INTEREST_RATE]:
    "comparableMonthlyInterestRate",
  [CustomerPreferredRateEnum.YEARLY_INTEREST_RATE]:
    "comparableYearlyInterestRate",
} as const;

export type RateKeysType =
  (typeof RATE_TO_BID_KEY_MAP)[keyof typeof RATE_TO_BID_KEY_MAP];

export const enum LoanApplicationVerificationFailureReasons {
  NOT_ELIGIBLE_FOR_HELP = "NOT_ELIGIBLE_FOR_HELP",
  DUPLICATE_EXISTS = "DUPLICATE_EXISTS",
  NO_COMPANY_DATA = "NO_COMPANY_DATA",
  IS_PEP = "IS_PEP",
  NO_SIGNING_RIGHTS = "NO_SIGNING_RIGHTS",
  NO_CREDIT_CHECK = "NO_CREDIT_CHECK",
}

export enum LoanApplicationStates {
  DRAFT = "DRAFT",
  CREATED = "CREATED",
  ADVICE = "ADVICE",
  OPEN_FOR_BIDS = "OPEN_FOR_BIDS",
  REJECTED = "REJECTED",
  NO_BIDS_RECEIVED = "NO_BIDS_RECEIVED",
  BIDS_REJECTED_BY_CUSTOMER = "BIDS_REJECTED_BY_CUSTOMER",
  BID_SELECTED = "BID_SELECTED",
  PAYOUT_RECEIVED_BY_CUSTOMER = "PAYOUT_RECEIVED_BY_CUSTOMER",
}

export enum LoanApplicationProduct {
  COMPANY_LOAN = "COMPANY_LOAN",
  FACTORING = "FACTORING",
  REAL_ESTATE = "REAL_ESTATE",
  LEASING = "LEASING",
}

export interface IAssignedToAdminUser {
  email: string;
  firstName: string;
  lastName: string;
  mobilePhoneNumber: string;
  profileImage?: {
    lowResolutionUrl: string;
  };
}

export enum AdviceCategoryEnum {
  SIMPLE = "SIMPLE",
  COMPLEX = "COMPLEX",
  DIGITAL_IMPROVEMENTS = "DIGITAL_IMPROVEMENTS",
  DIGITAL_REVENUE = "DIGITAL_REVENUE",
}

export interface ILoanApplication {
  id: string | undefined;
  state: LoanApplicationStates;
  requestedLoanAmount: number;

  contactPerson: IUser;
  applyingCompany: IApplyingCompany;
  product: LoanApplicationProduct;
  affiliateInfoJson?: {
    utm_source?: string | null;
    utm_campaign?: string | null;
  };
  currency: string;

  biddingEndsAt: string;
}

export interface ILoanApplicationExtended extends ILoanApplication {
  eligibleForLeasingBids: boolean;
  customerPreferredRate: CustomerPreferredRateEnum;
  alternativeRate: CustomerPreferredRateEnum;
  verificationFailureReasons: LoanApplicationVerificationFailureReasons[];

  applicationAttractiveness: number;
  assignedToAdminUser: IAssignedToAdminUser;
  forecastApprovedProbability: number | null;
  shouldTrackDraftConversion: boolean;
  shouldTrackRateForecastExecutedConversion: boolean;
  advisoryAcceptedAt: DateString | null;
  adviceCategory: AdviceCategoryEnum;
  isDigitalAdvice: boolean;
  initialNumberOfAttractivenessImprovements: number;

  latestDuplicatedApplicationInfo: ILatestDuplicatedApplicationInfo;
  applicationImprovements: IApplicationImprovement[];
  triageFields: string[];
  previewFields: string[];
}

export interface ICustomerCompany {
  id?: string;
  name: string;
  typeDisplayName?: string;
  organisationNumber: string;
}

export interface IBoardMemberRole {
  roleCode: string;
  roleName: string;
}

export interface IBoardMember {
  id?: string;
  ssn: string;
  firstName: string;
  lastName: string;
  roles: IBoardMemberRole[];
}

export interface ILatestDuplicatedApplicationInfo {
  id: number;
  numBids: number;
  submittedAt: DateString;
}

export interface IApplicationImprovement {
  field: string;
  displayText: string;
  type: AttractivenessImprovementTypeEnum;
}

export enum AttractivenessImprovementTypeEnum {
  REQUEST_HIGHER_AMOUNT = "REQUEST_HIGHER_AMOUNT",
  REQUEST_LOWER_AMOUNT = "REQUEST_LOWER_AMOUNT",
  ADD_AT_LEAST_ONE_GUARANTOR = "ADD_AT_LEAST_ONE_GUARANTOR",
  ADD_MORE_GUARANTORS = "ADD_MORE_GUARANTORS",
  PROVIDE_DETAILED_EXPLANATION = "PROVIDE_DETAILED_EXPLANATION",
  QUALIFY_FOR_FACTORING = "QUALIFY_FOR_FACTORING",
  ADD_REVENUE_LAST_YEAR = "ADD_REVENUE_LAST_YEAR",
  ADD_ACCOUNTING_DATA = "ADD_ACCOUNTING_DATA",
  ADD_REAL_ESTATE_ADDITIONAL_DATA = "ADD_REAL_ESTATE_ADDITIONAL_DATA",
  ADD_LEASING_ADDITIONAL_DATA = "ADD_LEASING_ADDITIONAL_DATA",
  QUALIFY_FOR_LEASING = "QUALIFY_FOR_LEASING",
}
